import React, { useState, useRef, useEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SelectOption {
  value: string
  label: string
  description?: string
  icon?: React.ComponentType<{ className?: string }>
  suffix?: React.ReactNode
  /** Render a horizontal divider in place of this option (separates option groups). */
  separator?: boolean
}

export interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  minWidth?: string
  showSelectedIcon?: boolean
  buttonContent?: React.ReactNode
  id?: string
  /**
   * Id of a visible field label. Use this rather than `<label htmlFor>`: a label
   * pointing at the trigger button forwards its clicks, so clicking the label
   * would toggle the list. The trigger is announced as "<label> <value>".
   */
  'aria-labelledby'?: string
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select option',
  className,
  disabled = false,
  minWidth = '140px',
  showSelectedIcon = false,
  buttonContent,
  id,
  'aria-labelledby': labelledBy
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const [isPositioned, setIsPositioned] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // Keyboard model mirrors a native <select>: focus never leaves the trigger, and
  // the highlighted option is announced through aria-activedescendant.
  const [activeValue, setActiveValue] = useState<string | null>(null)
  const typeahead = useRef({ text: '', at: 0 })
  const baseId = useId()
  const triggerId = id ?? `${baseId}-trigger`
  const listboxId = `${baseId}-listbox`
  const optionId = (index: number) => `${listboxId}-${index}`

  // Close dropdown when clicking outside and handle positioning
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setIsPositioned(false)
      }
    }

    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const buttonRect = buttonRef.current.getBoundingClientRect()
        
        // Estimate dropdown height more accurately
        // Check if any options have descriptions to adjust height calculation
        const hasDescriptions = options.some(option => option.description)
        // Each option is approximately 40px (py-2.5 padding + text + borders)
        // With description: approximately 64px (py-3 padding + label + description)
        const estimatedItemHeight = hasDescriptions ? 64 : 40
        // Add 8px for container padding (py-1)
        const containerPadding = 8
        const maxHeight = 320 // max-h-80 = 320px
        const estimatedHeight = Math.min(options.length * estimatedItemHeight + containerPadding, maxHeight + containerPadding)
        
        const spaceBelow = window.innerHeight - buttonRect.bottom - 20 // Add buffer
        const spaceAbove = buttonRect.top - 20 // Add buffer
        
        // Determine drop direction before rendering to prevent flicker
        const shouldDropUp = spaceBelow < estimatedHeight || (spaceAbove > estimatedHeight && spaceAbove > spaceBelow * 1.5)
        
        // Calculate position for portal with consistent 4px gap
        const gap = 4
        // Calculate dropdown width - ensure adequate space for icons and text
        const hasIconsOrSuffixes = options.some(option => option.icon || option.suffix)
        let dropdownWidth
        
        if (hasIconsOrSuffixes || hasDescriptions) {
          // For dropdowns with icons or descriptions, calculate width based on content needs
          const baseMinWidth = parseFloat(minWidth) || 140
          const buttonWidth = buttonRect.width
          
          // Find the longest option text to estimate required width
          const longestOptionLength = Math.max(...options.map(option => option.label.length))
          // Also consider description length if present
          const longestDescriptionLength = hasDescriptions 
            ? Math.max(...options.map(option => option.description?.length || 0))
            : 0
          
          // Estimate width needed: icon (16px) + gap (10px) + text + padding (24px) + buffer (20px)
          // For descriptions, we need more width to accommodate the additional text
          const textWidth = hasDescriptions 
            ? Math.max(longestOptionLength, longestDescriptionLength) * 7
            : longestOptionLength * 7
          const estimatedWidth = 16 + 10 + textWidth + 24 + 20
          
          // Use the larger of: estimated width, button width, or minWidth
          dropdownWidth = Math.max(estimatedWidth, buttonWidth, baseMinWidth)
        } else {
          dropdownWidth = Math.max(buttonRect.width, parseFloat(minWidth) || 140)
        }
        
        // Check if dropdown would overflow the right edge of the viewport
        let leftPosition = buttonRect.left
        const rightEdge = leftPosition + dropdownWidth
        const viewportWidth = window.innerWidth
        const padding = 20 // Add some padding from viewport edge
        
        if (rightEdge > viewportWidth - padding) {
          // Adjust left position to prevent overflow
          leftPosition = viewportWidth - dropdownWidth - padding
          // Ensure it doesn't go off the left edge either
          leftPosition = Math.max(padding, leftPosition)
        }
        
        // Set both dropUp state and position in sync
        setDropUp(shouldDropUp)
        setDropdownPosition({
          // Once the list is on screen, its real height: a re-position (the list
          // itself scrolling, arrow keys included) must not snap an upward list
          // back to the estimate and leave it detached from the trigger.
          top: shouldDropUp ? buttonRect.top - (listRef.current?.offsetHeight ?? estimatedHeight) - gap : buttonRect.bottom + gap,
          left: leftPosition,
          width: dropdownWidth
        })
        setIsPositioned(true)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Use setTimeout to ensure DOM has updated before positioning
      setTimeout(updatePosition, 0)
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    } else {
      // Reset positioning state when closed
      setIsPositioned(false)
      setDropUp(false)
    }
  }, [isOpen, options.length])

  // Fine-tune position after dropdown is rendered with actual height (without changing drop direction)
  useEffect(() => {
    if (isOpen && dropdownRef.current && buttonRef.current && isPositioned) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const actualDropdownHeight = dropdownRef.current.offsetHeight
      const gap = 4
      
      // Only fine-tune the position if we're already dropping up
      // Don't change the drop direction to prevent flicker
      if (dropUp) {
        setDropdownPosition(prev => ({
          ...prev,
          top: buttonRect.top - actualDropdownHeight - gap
        }))
      }
    }
  }, [isOpen, isPositioned, dropUp])

  const selectedOption = options.find(option => option.value === value)
  const displayText = selectedOption ? selectedOption.label : placeholder

  const close = () => {
    setIsOpen(false)
    setIsPositioned(false)
  }

  const setActiveIndex = (index: number) => setActiveValue(index === -1 ? null : options[index].value)

  const open = (index: number) => {
    setActiveIndex(index)
    setIsOpen(true)
  }

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue)
    close()
  }

  const selectable = options.flatMap((option, index) => (option.separator ? [] : [index]))
  const selectedIndex = options.findIndex(option => !option.separator && option.value === value)
  // Tracked by value, so a highlight follows its option when the list loads,
  // filters or reorders while open; an option that is gone means no highlight.
  const highlighted = options.findIndex(option => !option.separator && option.value === activeValue)

  // Within 500ms of the last typed letter, a space continues the search
  // ("Billing admin") instead of picking the highlighted option.
  const isSearching = () => typeahead.current.text !== '' && Date.now() - typeahead.current.at <= 500

  // Next selectable index from `from` in `step` direction, clamped at the ends.
  const step = (from: number, delta: 1 | -1) => {
    const pos = selectable.indexOf(from)
    if (pos === -1) return delta === 1 ? selectable[0] ?? -1 : selectable[selectable.length - 1] ?? -1
    return selectable[Math.min(Math.max(pos + delta, 0), selectable.length - 1)]
  }

  // Type-to-find, as a native select does: keys typed within 500ms build one
  // prefix; a repeated single letter cycles through the options starting with it.
  const findByTypeahead = (char: string, from: number) => {
    const now = Date.now()
    const buffer = typeahead.current
    buffer.text = now - buffer.at > 500 ? char : buffer.text + char
    buffer.at = now
    const query = buffer.text.toLowerCase()
    const cycling = query.split('').every(c => c === query[0])
    const prefix = cycling ? query[0] : query
    // Search from the current option (after it, when cycling); with no current
    // option, from the top.
    const pos = selectable.indexOf(from)
    const begin = pos === -1 ? 0 : pos + (cycling ? 1 : 0)
    const ordered = [...selectable.slice(begin), ...selectable.slice(0, begin)]
    return ordered.find(index => options[index].label.toLowerCase().startsWith(prefix)) ?? -1
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        open(selectedIndex !== -1 ? selectedIndex : step(-1, 1))
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Unlike a native select, typing on a closed list opens it at the match
        // rather than changing the value: many consumers act on every change
        // (save immediately, apply a template), and a value changing letter by
        // letter would fire that action once per keystroke.
        const match = findByTypeahead(e.key, selectedIndex)
        if (match !== -1) open(match)
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        // Close only the list. Stopping here keeps an enclosing modal's
        // document-level Escape handler from closing the whole dialog.
        e.preventDefault()
        e.stopPropagation()
        close()
        return
      case 'Tab':
        close()
        return
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex(step(highlighted, e.key === 'ArrowDown' ? 1 : -1))
        return
      case 'Home':
      case 'End':
        e.preventDefault()
        setActiveIndex(e.key === 'Home' ? selectable[0] ?? -1 : selectable[selectable.length - 1] ?? -1)
        return
      case 'Enter':
      case ' ': {
        e.preventDefault()
        if (e.key === ' ' && isSearching()) {
          const match = findByTypeahead(' ', highlighted)
          if (match !== -1) setActiveIndex(match)
        } else if (highlighted !== -1) {
          handleOptionClick(options[highlighted].value)
        } else {
          close()
        }
        return
      }
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const match = findByTypeahead(e.key, highlighted)
          if (match !== -1) setActiveIndex(match)
        }
    }
  }

  // Firefox fires a button's click on Space *release*; keydown already acted, so
  // without this the list would open and immediately close again.
  const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ') e.preventDefault()
  }

  // Keep the highlighted option in view as the arrow keys move it.
  useEffect(() => {
    if (!isOpen || !isPositioned || highlighted === -1) return
    document.getElementById(optionId(highlighted))?.scrollIntoView?.({ block: 'nearest' })
  }, [isOpen, isPositioned, highlighted, listboxId])

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        id={triggerId}
        name={id ? `${id}-input` : 'select-input'}
        onClick={() => {
          if (!disabled) {
            if (isOpen) {
              close()
            } else {
              open(selectedIndex)
            }
          }
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen && isPositioned && highlighted !== -1 ? optionId(highlighted) : undefined}
        aria-labelledby={labelledBy ? `${labelledBy} ${triggerId}` : undefined}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-3 py-2 border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors',
          !disabled && 'hover:bg-gray-50 dark:hover:bg-white/10',
          disabled && 'opacity-50 cursor-not-allowed',
          'field-text'
        )}
        style={{ minWidth }}
      >
        {buttonContent ? buttonContent : (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {showSelectedIcon && selectedOption?.icon && (
              <selectedOption.icon className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate">{displayText}</span>
          </div>
        )}
        <ChevronDown 
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform flex-shrink-0',
            isOpen && 'transform rotate-180'
          )} 
        />
      </button>
      
      {isOpen && isPositioned && !disabled && createPortal(
        <div 
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[100001]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
          ref={(node) => {
            dropdownRef.current = node
            listRef.current = node
          }}
        >
          <div className="py-1 max-h-80 overflow-y-auto" role="listbox" id={listboxId} aria-labelledby={labelledBy ?? triggerId}>
            {options.map((option, idx) => {
              if (option.separator) {
                return <div key={`sep-${idx}`} role="separator" className="my-1 border-t border-gray-200 dark:border-gray-700" />
              }
              return (
              <div
                key={option.value}
                id={optionId(idx)}
                role="option"
                aria-selected={value === option.value}
                // Keep focus on the trigger so the keyboard model stays in one place.
                onMouseDown={(e) => e.preventDefault()}
                // Pointer movement, not mouseenter: arrow keys scroll options under
                // a resting pointer, and mouseenter would steal the highlight back.
                onMouseMove={() => { if (highlighted !== idx) setActiveIndex(idx) }}
                onClick={() => handleOptionClick(option.value)}
                className={cn(
                  'w-full px-3 text-left text-sm cursor-pointer transition-colors flex items-start gap-2.5',
                  option.description ? 'py-3' : 'py-2.5',
                  value === option.value 
                    ? 'bg-primary/10 text-primary dark:bg-primary/15' 
                    : 'text-foreground',
                  idx === highlighted && (value === option.value
                    ? 'bg-primary/15 dark:bg-primary/25 dark:text-[hsl(var(--primary-hover))]'
                    : 'bg-gray-100 dark:bg-gray-700')
                )}
              >
                {option.icon && (
                  <option.icon className={cn(
                    "h-4 w-4 flex-shrink-0",
                    option.description ? "mt-0.5" : ""
                  )} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{option.label}</div>
                  {option.description && (
                    <div className={cn(
                      "text-xs mt-0.5",
                      value === option.value
                        ? "text-primary/80"
                        : "text-gray-500 dark:text-gray-400"
                    )}>
                      {option.description}
                    </div>
                  )}
                </div>
                {option.suffix}
              </div>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
} 